import React, { useEffect, useState, useMemo } from 'react'
import {
  FocusContext,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import classNames from 'classnames'
import LogoHeader from '../../components/LogoHeader'
import FocusableLink from '../../components/FocusableLink'
import { platformTransitionStyle, throttle } from '../../../../shared/helpers'

import type { SeasonsProps } from './types'
import type { FocusableComponentLayout } from '@noriginmedia/norigin-spatial-navigation'

const LIST_TOP_OFFSET = 325
const SEASON_ITEM_HEIGHT = 96

const Seasons = (props: SeasonsProps) => {
  const { ref, focusKey, hasFocusedChild } = useFocusable({
    focusKey: 'SEASONS',
    trackChildren: true,
    onBlur: () =>
      props.setFocusedState({ seasons: 'blurred', seasonContent: 'focused' }),
  })
  const [translateY, setTranslateY] = useState(0)
  const [prevSeasonItemIndexFocused, setPrevSeasonItemIndexFocused] = useState()
  const [currentSeasonItemIndexFocused, setCurrentSeasonItemIndexFocused] =
    useState(0)

  useEffect(() => {
    if (hasFocusedChild) {
      props.setFocusedState({ seasons: 'focused', seasonContent: 'blurred' })
    }
  }, [hasFocusedChild])

  useEffect(() => () => setPrevSeasonItemIndexFocused(undefined), [])

  const handleBlur = (_, extraProps) =>
    setPrevSeasonItemIndexFocused(extraProps.index)

  const handleFocus = (layout: FocusableComponentLayout, extraProps) => {
    setCurrentSeasonItemIndexFocused(extraProps.index)
    props.changeSeasonFocused(extraProps.activeSeason)

    if (props.seasons.length <= 3) {
      setTranslateY(0)
      return
    }

    if ([0, 1].includes(extraProps.index)) {
      setTranslateY(
        -(layout.top - extraProps.index * SEASON_ITEM_HEIGHT - LIST_TOP_OFFSET)
      )
    } else if (extraProps.index > 1) {
      setTranslateY(-(layout.top - LIST_TOP_OFFSET - SEASON_ITEM_HEIGHT))
    }
  }

  const seasonsItemClasses = (index) =>
    classNames('seasons__season-item', {
      'seasons__season-item--prev-focused':
        props.loading && index === prevSeasonItemIndexFocused,
      'seasons__season-item--hidden':
        props.seasons.length > 3 && currentSeasonItemIndexFocused > index + 1,
    })

  const handleWheel = useMemo(
    () =>
      throttle((event: React.WheelEvent<HTMLDivElement>) => {
        const deltaY = event.deltaY
        const scrollStep = SEASON_ITEM_HEIGHT
        let newTranslateY = translateY

        if (deltaY > 0) {
          newTranslateY = Math.max(
            -(props.seasons.length - 1) * SEASON_ITEM_HEIGHT,
            translateY - scrollStep
          )
        } else if (deltaY < 0) {
          newTranslateY = Math.min(0, translateY + scrollStep)
        }

        const scrollPosition = Math.abs(newTranslateY)
        let activeSeasonIndex = Math.floor(scrollPosition / SEASON_ITEM_HEIGHT)
        activeSeasonIndex = Math.max(
          0,
          Math.min(activeSeasonIndex, props.seasons.length - 1)
        )

        if (activeSeasonIndex !== currentSeasonItemIndexFocused) {
          setCurrentSeasonItemIndexFocused(activeSeasonIndex)
          props.changeSeasonFocused(props.seasons[activeSeasonIndex])
        }

        setTranslateY(newTranslateY)
      }, 600),
    [props.seasons.length, translateY, currentSeasonItemIndexFocused, props]
  )

  const handleEnterPress = (extraProps) => {
    props.changeSeasonFocused(extraProps.activeSeason)
  }

  return (
    <div className="seasons">
      <LogoHeader className="seasons__logo-header" />
      <h2 className="seasons__show-title">{props.showTitle}</h2>
      <div className="seasons__container" onWheel={handleWheel}>
        <FocusContext.Provider value={focusKey}>
          <div
            ref={ref}
            className="seasons__list"
            style={{
              transform: `translateY(${translateY}px)`,
              transition: platformTransitionStyle(
                'transform 400ms ease-in-out'
              ),
            }}
          >
            {props.seasons.map((season, index) => (
              <FocusableLink
                key={season.cid}
                focusKey={`${props.seasonType}-${season.cid}`}
                className={seasonsItemClasses(index)}
                extraProps={{ activeSeason: season, index }}
                onEnterPress={handleEnterPress}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onArrowPress={props.onArrowPress}
              >
                {season.ordinal ? `Season ${season.ordinal}` : season.title}
              </FocusableLink>
            ))}
          </div>
        </FocusContext.Provider>
      </div>
    </div>
  )
}

export default Seasons
